/**
 * The response is the same shape as the read, so it seeds the read cache directly rather
 * than costing a refetch round-trip. The file detail still refetches: approving the last
 * step completes the chain, which moves the file's status. Toast and confirm UX belong to
 * the caller.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { approveAssetStep } from './approval-actions.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { approveAssetStep };

interface ApproveAssetStepVariables {
  assetId: string;
  /** Required on any-order chains, where more than one step can be actionable at once. */
  stepId?: string;
}

interface UseApproveAssetStepOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after approving. */
  folderId?: string;
}

export function useApproveAssetStep(options: UseApproveAssetStepOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ assetId, stepId }: ApproveAssetStepVariables) =>
      approveAssetStep(projectId, assetId, stepId),
    onSuccess: (data, { assetId }) => {
      queryClient.setQueryData(ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId), data);
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_step_approved', { project_id: projectId });
    },
  });
}
